'use client'

import { Children } from 'react'
import * as styles from './Button.css'
import Link, { type LinkProps } from 'next/link'
import { Button as BaseButton } from '@ariakit/react'
import { type RecipeVariants } from '@vanilla-extract/recipes'
import { type LucideIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip'
import Spinner from '../Spinner/Spinner'

type ButtonVariants = RecipeVariants<typeof styles.buttonRecipe>

// Icon size mapping for different button sizes
const iconSizeMap = {
  tiny: 16,
  small: 16,
  medium: 20,
  large: 20,
} as const

export type ButtonProps = {
  as?: React.ElementType
  /** When `href` is present a `a` tag is rendered together with Next.js's Link component */
  href?: LinkProps['href']
  /** If only an icon is provided and no children, then `square` is set to `true` automatically */
  icon?: LucideIcon
  /** An icon after the label, e.g. a chevron on a menu trigger. */
  trailingIcon?: LucideIcon
  /** Disables the button; a spinner replaces the icon, or the label without one. */
  loading?: boolean
  /** Same props as Next.js's Link component */
  linkProps?: Omit<LinkProps, 'href'>
  /** Optional tooltip content rendered with Ariakit Tooltip */
  tooltip?: React.ReactNode
} & ButtonVariants &
  React.ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    ref?: React.RefObject<HTMLButtonElement | null>
  }

/**
 * While loading, the spinner takes the icon's slot (the leading one, else the
 * trailing one) so the button keeps its size. Without an icon it covers the
 * hidden label instead: a spinner beside the label would widen the button.
 */
function ButtonContent({
  icon: Icon,
  trailingIcon: TrailingIcon,
  iconSize,
  loading,
  children,
}: {
  icon?: LucideIcon
  trailingIcon?: LucideIcon
  iconSize: number
  loading: boolean
  children: React.ReactNode
}) {
  if (loading && !Icon && !TrailingIcon) {
    return (
      <>
        <span className={styles.loadingContent}>{children}</span>
        <span className={styles.spinner}>
          <Spinner size={iconSize} />
        </span>
      </>
    )
  }
  const spinner = <Spinner size={iconSize} className={styles.iconSpinner} />
  return (
    <>
      {Icon && (loading ? spinner : <Icon size={iconSize} />)}
      {children}
      {TrailingIcon &&
        (loading && !Icon ? spinner : <TrailingIcon size={iconSize} />)}
    </>
  )
}

/**
 * The suite's button. A toggle passes `aria-pressed` and a trigger gets
 * `aria-expanded` from Ariakit; both render the pressed background.
 */
export default function Button({
  children,
  className = '',
  size = 'medium',
  variant = 'raised',
  circular = false,
  square = false,
  icon: Icon,
  trailingIcon: TrailingIcon,
  loading = false,
  href,
  linkProps = {},
  as: As = 'button',
  tooltip,
  ...props
}: ButtonProps) {
  const isIconOnly =
    Icon !== undefined && Children.toArray(children).length === 0
  square = isIconOnly || square

  const isExternal = typeof href === 'string' && href.startsWith('https://')
  if (isExternal) {
    props.rel = 'noopener'
    props.target = '_blank'
  }
  const button = (
    <BaseButton
      render={(props) =>
        href !== undefined ? (
          <Link {...linkProps} href={href} {...props} />
        ) : (
          <As {...props} />
        )
      }
      className={`${styles.buttonRecipe({ size, variant, circular, square })} ${className}`}
      {...props}
      aria-busy={loading || undefined}
      disabled={loading || props.disabled}
    >
      <ButtonContent
        icon={Icon}
        trailingIcon={TrailingIcon}
        iconSize={iconSizeMap[size]}
        loading={loading}
      >
        {children}
      </ButtonContent>
    </BaseButton>
  )

  if (tooltip === undefined) return button

  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}
